 // frontend/src/components/TextEditorComponent.jsx
import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Strike from '@tiptap/extension-strike';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import CharacterCount from '@tiptap/extension-character-count';
import ImageResize from 'tiptap-extension-resize-image';

import {
    Box,
    IconButton,
    Divider,
    Tooltip,
    Typography,
    Paper
} from '@mui/material';

import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    StrikethroughS,
    Subscript as SubscriptIcon,
    Superscript as SuperscriptIcon,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    FormatAlignJustify,
    FormatListBulleted,
    FormatListNumbered,
    FormatIndentIncrease,
    FormatIndentDecrease,
    Link as LinkIcon,
    Image as ImageIcon,
    TableChart,
    HorizontalRule,
    Code,
    Fullscreen,
    FullscreenExit,
    Undo,
    Redo,
    TextFields,
    FormatColorText,
    FormatColorFill,
    FormatClear
} from '@mui/icons-material';

const TextEditorComponent = forwardRef(({ onChange, initialContent, maxCharCount }, ref) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const fileInputRef = useRef(null);

    const editorExtensions = [
        StarterKit.configure({ history: true, heading: { levels: [1, 2, 3] } }),
        Underline,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Subscript,
        Superscript,
        Strike,
        Link.configure({
            openOnClick: false,
            autolink: true,
            defaultProtocol: 'https',
            HTMLAttributes: {
                rel: 'noopener noreferrer',
                class: 'text-blue-500 hover:underline',
            },
        }),
        ImageResize.configure({
            allowBase64: true,
            inline: true,
            HTMLAttributes: {
                class: 'rounded-lg border cursor-move',
                draggable: 'true'
            }
        }),
        Table.configure({
            resizable: true,
            HTMLAttributes: { class: 'border-collapse' },
        }),
        TableRow,
        TableHeader,
        TableCell,
        CharacterCount.configure({ limit: maxCharCount }),
    ];

    const editor = useEditor({
        extensions: editorExtensions,
        content: initialContent || '',
        onUpdate: ({ editor }) => {
            if (onChange) {
                onChange({
                    html: editor.getHTML(),
                    json: editor.getJSON()
                });
            }
        },
        editorProps: {
            handleDrop: (view, event, slice, moved) => {
                if (!moved && event.dataTransfer.files && event.dataTransfer.files[0]) {
                    const file = event.dataTransfer.files[0];
                    if (file.type.includes('image/')) {
                        handleImageUpload(file);
                        return true;
                    }
                }
                return false;
            },
            attributes: {
                class: 'prose dark:prose-invert max-w-none focus:outline-none p-4 min-h-[300px]',
            },
        },
    });

    const handleImageUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        const imageUrl = e.target.result;
        editor.chain().focus().setImage({ src: imageUrl }).run();
    };
    reader.readAsDataURL(file);
};


    const triggerImageUpload = () => fileInputRef.current.click();

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) handleImageUpload(file);
    };

    const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

    useImperativeHandle(ref, () => ({
        setContents: (content) => {
            if (!editor) return false;
            try {
                if (typeof content === 'string' && content.startsWith('<')) {
                    editor.commands.setContent(content);
                } else if (typeof content === 'string') {
                    editor.commands.setContent(JSON.parse(content));
                } else {
                    editor.commands.setContent(content);
                }
                return true;
            } catch (error) {
                console.error("Error setting content:", error);
                return false;
            }
        },
        setContentAndGetHTML: (content) => {
            if (!editor) return null;
            try {
                const contentToSet = typeof content === 'string' ? JSON.parse(content) : content;
                editor.commands.setContent(contentToSet);
                return editor.getHTML();
            } catch (error) {
                console.error("Content conversion error:", error);
                return null;
            }
        },
        getContents: () => editor?.getJSON(),
        getHTML: () => editor?.getHTML(),
        clearEditor: () => editor?.commands.clearContent(),
    }));

    useEffect(() => {
        if (editor && initialContent !== undefined && initialContent !== null) {
            let contentToSet = initialContent;
            if (typeof initialContent === 'string') {
                try {
                    contentToSet = JSON.parse(initialContent);
                } catch (e) {
                    editor.commands.setContent(initialContent);
                    return;
                }
            }
            if (JSON.stringify(editor.getJSON()) !== JSON.stringify(contentToSet)) {
                editor.commands.setContent(contentToSet, false);
            }
        } else if (editor && editor.getHTML() !== '<p></p>' && editor.getHTML() !== '') {
            editor.commands.setContent('');
        }
    }, [editor, initialContent]);

    if (!editor) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                <Typography>Loading editor...</Typography>
            </Box>
        );
    }

    return (
          <Paper elevation={3} sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '400px',  
            ...(isFullscreen && {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,  
                m: 0,
                height: '100vh',
                width: '100vw',  
            })
        }}>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: 'none' }}
            />

            {/* Toolbar */}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.5,
                p: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper'
            }}>
                {/* History */}
                <Tooltip title="Undo">
                    <IconButton
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                        size="small"
                    >
                        <Undo fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Redo">
                    <IconButton
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                        size="small"
                    >
                        <Redo fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Text Formatting */}
                <Tooltip title="Bold">
                    <IconButton
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        color={editor.isActive('bold') ? 'primary' : 'default'}
                        size="small"
                    >
                        <FormatBold fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Italic">
                    <IconButton
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        color={editor.isActive('italic') ? 'primary' : 'default'}
                        size="small"
                    >
                        <FormatItalic fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Underline">
                    <IconButton
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        color={editor.isActive('underline') ? 'primary' : 'default'}
                        size="small"
                    >
                        <FormatUnderlined fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Strikethrough">
                    <IconButton
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        color={editor.isActive('strike') ? 'primary' : 'default'}
                        size="small"
                    >
                        <StrikethroughS fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Subscript">
                    <IconButton
                        onClick={() => editor.chain().focus().toggleSubscript().run()}
                        color={editor.isActive('subscript') ? 'primary' : 'default'}
                        size="small"
                    >
                        <SubscriptIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Superscript">
                    <IconButton
                        onClick={() => editor.chain().focus().toggleSuperscript().run()}
                        color={editor.isActive('superscript') ? 'primary' : 'default'}
                        size="small"
                    >
                        <SuperscriptIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Alignment */}
                <Tooltip title="Align Left">
                    <IconButton
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                        color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
                        size="small"
                    >
                        <FormatAlignLeft fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Align Center">
                    <IconButton
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                        color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
                        size="small"
                    >
                        <FormatAlignCenter fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Align Right">
                    <IconButton
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}
                        color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
                        size="small"
                    >
                        <FormatAlignRight fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Justify">
                    <IconButton
                        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                        color={editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'}
                        size="small"
                    >
                        <FormatAlignJustify fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Lists */}
                <Tooltip title="Bullet List">
                    <IconButton
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        color={editor.isActive('bulletList') ? 'primary' : 'default'}
                        size="small"
                    >
                        <FormatListBulleted fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Numbered List">
                    <IconButton
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        color={editor.isActive('orderedList') ? 'primary' : 'default'}
                        size="small"
                    >
                        <FormatListNumbered fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Indent">
                    <IconButton
                        onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
                        disabled={!editor.can().sinkListItem('listItem')}
                        size="small"
                    >
                        <FormatIndentIncrease fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Outdent">
                    <IconButton
                        onClick={() => editor.chain().focus().liftListItem('listItem').run()}
                        disabled={!editor.can().liftListItem('listItem')}
                        size="small"
                    >
                        <FormatIndentDecrease fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Insert Elements */}
                <Tooltip title="Link">
                    <IconButton
                        onClick={() => {
                            const previousUrl = editor.getAttributes('link').href;
                            const url = window.prompt('URL', previousUrl);
                            if (url === null) return;
                            if (url === '') {
                                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                                return;
                            }
                            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
                        }}
                        color={editor.isActive('link') ? 'primary' : 'default'}
                        size="small"
                    >
                        <LinkIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Insert Image">
                    <IconButton
                        onClick={triggerImageUpload}
                        size="small"
                    >
                        <ImageIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Insert Table">
                    <IconButton
                        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                        size="small"
                    >
                        <TableChart fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Horizontal Line">
                    <IconButton
                        onClick={() => editor.chain().focus().setHorizontalRule().run()}
                        size="small"
                    >
                        <HorizontalRule fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Code Block">
                    <IconButton
                        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                        color={editor.isActive('codeBlock') ? 'primary' : 'default'}
                        size="small"
                    >
                        <Code fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* Fullscreen */}
                <Box sx={{ flexGrow: 1 }} /> {/* This pushes Fullscreen button to the right */}
                <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                    <IconButton
                        onClick={toggleFullscreen}
                        size="small"
                    >
                        {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Editor Content */}
            <Box
                sx={{
                    flexGrow: 1, // Allows content area to fill available space
                    overflowY: 'auto', // Add scrollbar if content overflows
                    position: 'relative',
                    '& .ProseMirror': {
                        p: 2,
                        minHeight: '300px', // Minimum height for the editor area
                        outline: 'none', // Remove focus outline
                        // Add basic ProseMirror styles here or in a global CSS file
                        '& p': { margin: '0 0 1em' },
                        '& ul, & ol': { margin: '1em 0', paddingLeft: '1.5em' },
                        '& li > p': { margin: 0 }, // No extra margin inside list items
                        '& h1, & h2, & h3, & h4, & h5, & h6': { margin: '1em 0 0.5em', lineHeight: 1.2 },
                        // Table styling for ProseMirror
                        '& table': { borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' },
                        '& th, & td': { border: '1px solid #ccc', padding: '8px', textAlign: 'left', verticalAlign: 'top' },
                        '& img': { maxWidth: '100%', height: 'auto' }, // Ensure images are responsive
                        '& blockquote': {
                            borderLeft: '4px solid #ccc',
                            margin: '1.5em 10px',
                            padding: '0.5em 10px',
                            color: '#666'
                        },
                        '& code': {
                            backgroundColor: 'rgba(97, 97, 97, .1)',
                            color: '#616161',
                            padding: '.2em .4em',
                            borderRadius: '5px'
                        },
                        '& pre': {
                            backgroundColor: '#0d0d0d',
                            color: '#fff',
                            fontFamily: 'monospace',
                            padding: '.75rem 1rem',
                            borderRadius: '0.5rem',
                            '& code': {
                                color: 'inherit',
                                padding: 0,
                                background: 'none',
                                fontSize: '0.8em'
                            }
                        }
                    }
                }}
            >
                {/* Bubble Menu for table operations */}
                {editor && (
                    <BubbleMenu
                        className="bubble-menu"
                        tippyOptions={{ duration: 100 }}
                        editor={editor}
                        shouldShow={({ editor, view, state, oldState, from, to }) => {
                            // only show if selection is inside a table
                            return editor.isActive('table');
                        }}
                    >
                        <Box sx={{ p: 1, display: 'flex', gap: 0.5, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 3 }}>
                            <Tooltip title="Add Column Before">
                                <IconButton onClick={() => editor.chain().focus().addColumnBefore().run()} size="small"><TextFields fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Add Column After">
                                <IconButton onClick={() => editor.chain().focus().addColumnAfter().run()} size="small"><TextFields fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Column">
                                <IconButton onClick={() => editor.chain().focus().deleteColumn().run()} size="small"><FormatClear fontSize="small" /></IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <Tooltip title="Add Row Before">
                                <IconButton onClick={() => editor.chain().focus().addRowBefore().run()} size="small"><TextFields fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Add Row After">
                                <IconButton onClick={() => editor.chain().focus().addRowAfter().run()} size="small"><TextFields fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Row">
                                <IconButton onClick={() => editor.chain().focus().deleteRow().run()} size="small"><FormatClear fontSize="small" /></IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <Tooltip title="Delete Table">
                                <IconButton onClick={() => editor.chain().focus().deleteTable().run()} size="small"><TableChart fontSize="small" /></IconButton>
                            </Tooltip>
                        </Box>
                    </BubbleMenu>
                )}
                <EditorContent editor={editor} />
            </Box>

            {/* Status Bar */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default'
            }}>
                <Typography variant="caption" color="textSecondary">
                    {editor.storage.characterCount?.words()} words | {editor.storage.characterCount?.characters()} characters
                    {maxCharCount && ` / ${maxCharCount}`}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                    {editor.isActive('heading', { level: 1 }) && 'Heading 1'}
                    {editor.isActive('heading', { level: 2 }) && 'Heading 2'}
                    {editor.isActive('heading', { level: 3 }) && 'Heading 3'}
                    {editor.isActive('paragraph') && 'Paragraph'}
                    {/* You can add more active type indicators here */}
                </Typography>
            </Box>
        </Paper>
    );
});

export default TextEditorComponent;
